import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { MaskedLine, Overline } from "../Reveal";
import { HERO_IMAGE } from "../../data/modules";
import { scrollToId } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Océan calme sous un ciel lumineux" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-white/35" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-paper" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-28 pt-40 w-full">
        <MaskedLine delay={0.15}>
          <Overline>{t("hero.overline")}</Overline>
        </MaskedLine>
        <h1 className="mt-8 font-serif text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] text-ink">
          <MaskedLine delay={0.3}>{t("hero.t1")}</MaskedLine>
          <MaskedLine delay={0.45}>
            <span>{t("hero.t2a")} <em className="italic text-sagedark">{t("hero.t2b")}</em></span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.65} className="mt-10 max-w-xl">
          <p className="text-base md:text-lg leading-relaxed text-smoke">
            {t("hero.sub")}
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <Link
            data-testid="hero-cta-simulateur"
            to="/simulateur"
            className="rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide hover:bg-sagedark transition-colors duration-300"
          >
            {t("hero.cta1")}
          </Link>
          <button
            data-testid="hero-cta-modules"
            onClick={() => scrollToId("modules")}
            className="rounded-full border border-ink/25 text-ink px-8 py-4 text-sm tracking-wide hover:border-ink hover:bg-white/40 transition-colors duration-300"
          >
            {t("hero.cta2")}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("manifeste")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 md:right-12 text-ink/60 hover:text-ink transition-colors duration-300"
        aria-label="Défiler vers le manifeste"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ArrowDown size={22} strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
};
